import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos ---------------------- */
export type Produto = {
  codigo: string;
  ncm: string;
  nome: string;
  classificacao: string;
  posicao: string;
  custo_unitario: number | string | null;
  moeda?: "USD" | "EUR" | "BRL" | string; // ← agora vem da API
};

type ProdutoRaw = Produto & { ocultar?: boolean };

type ListaResp = {
  fonte?: "cache" | "banco";
  versao?: number | null;
  data: Produto[]; // ← já virá só com visíveis
};

type AwaitResult = { changed: boolean; versao: number | null };

/** ---------------------- Constantes ---------------------- */
const BASE = "https://api.eva-googlemarine.com/departamentos/suprimentos";
const URL = `${BASE}/produtos`;
const URL_AWAIT = `${BASE}/produtos/await`;

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
// Segure próximo do hold do servidor p/ reduzir chamadas e evitar sobreposição
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120; // pequena folga entre awaits
const ACCESS_TOKEN_KEY = "access_token";

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function produtosMudaram(a: Produto[], b: Produto[]): boolean {
  if (a.length !== b.length) return true;
  return JSON.stringify(a) !== JSON.stringify(b);
}

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** ---------------------- HTTP helpers ---------------------- */
async function headVersao(signal?: AbortSignal): Promise<number | null> {
  try {
    const token = getAccessToken();
    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(URL, {
      method: "HEAD",
      headers,
      signal,
    });
    if (!res.ok) return null;
    const v = res.headers.get("x-cache-ver");
    const n = v ? Number(v) : null;
    return Number.isFinite(n!) ? n : null;
  } catch {
    return null;
  }
}

async function getLista(
  _versaoAtual: number | null,
  _signal?: AbortSignal
): Promise<ListaResp> {
  // 🔑 agora também envia o access_token no GET
  const token = getAccessToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(URL, {
    method: "GET",
    headers,
    cache: "no-store",
  });

  if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);

  const fonte = (res.headers.get("x-source") as "cache" | "banco") || undefined;
  const vh = res.headers.get("x-cache-ver");
  const verHdr = vh ? Number(vh) : null;

  const json = await res.json();
  const verBody = typeof json?.versao === "number" ? json.versao : null;
  const versao = Number.isFinite(verHdr!) ? verHdr : verBody;

  // Preferir 'visiveis' quando o backend já splitou,
  // senão, filtra 'data' removendo { ocultar: true }.
  const visiveisRaw: ProdutoRaw[] | undefined = Array.isArray(json?.visiveis)
    ? (json.visiveis as ProdutoRaw[])
    : undefined;

  const dataRaw: ProdutoRaw[] =
    visiveisRaw ?? (json?.data as ProdutoRaw[]) ?? [];

  // Se não veio 'visiveis', faz o filtro local; se a flag não existir, mantém.
  const onlyVisible: ProdutoRaw[] =
    visiveisRaw != null
      ? visiveisRaw
      : dataRaw.filter((p) => p?.ocultar !== true);

  // Remover a chave 'ocultar' do shape exposto (mantendo 'moeda' e demais)
  const cleanVisible: Produto[] = onlyVisible.map(
    ({ ocultar, ...rest }) => rest
  );

  return {
    fonte,
    versao: versao ?? null,
    data: cleanVisible,
  };
}

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${since}&timeout_ms=${AWAIT_TIMEOUT_MS}`;

    // 🔑 também envia Authorization no /await (se o endpoint for protegido)
    const token = getAccessToken();
    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(url, {
      method: "GET",
      headers,
      signal,
    });
    if (!res.ok) return null;

    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);

    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
export function useProdutosDisponiveis() {
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(true);
  const [fonte, setFonte] = useState<"cache" | "banco" | undefined>(undefined);
  const [versao, _setVersao] = useState<number | null>(null);

  const versaoRef = useRef<number | null>(null);
  const stop = useRef(false);

  const fetchingRef = useRef(false); // TRUE enquanto GET /produtos está ativo
  const awaitCtrlRef = useRef<AbortController | null>(null); // controller do /await atual
  const awaitSupported = useRef<boolean | null>(null); // auto-detecção /await
  const awaitInFlightRef = useRef(false); // <-- single-flight para /await

  const setVersao = (v: number | null) => {
    versaoRef.current = v;
    _setVersao(v);
  };

  async function carregarLista(cur: number | null) {
    fetchingRef.current = true;
    try {
      const lista = await getLista(cur /* sem signal */);
      if (lista.versao != null) setVersao(lista.versao);
      setProdutos((prev) =>
        produtosMudaram(prev, lista.data) ? lista.data : prev
      );
      setFonte(lista.fonte);
    } finally {
      fetchingRef.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;

    (async () => {
      try {
        const vHead = await headVersao(); // opcional
        await carregarLista(vHead ?? null);
        setLoading(false);

        while (!stop.current) {
          // Evita GET simultâneo
          while (fetchingRef.current && !stop.current) {
            await sleep(25);
          }
          if (stop.current) break;

          const cur = versaoRef.current;

          // ---------- Long-poll preferencial ----------
          if (awaitSupported.current !== false && cur != null) {
            // Garante single-flight: se já tem /await pendente, aguarde
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const acAwait = new AbortController();
            awaitCtrlRef.current = acAwait;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;
            try {
              resp = await awaitChange(cur, acAwait.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                // erro transitório
                await sleep(ERROR_BACKOFF_MS);
              }
            } finally {
              // limpa controles
              if (awaitCtrlRef.current === acAwait) awaitCtrlRef.current = null;
              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            const avancou = resp ? (resp.versao ?? -1) > cur : false;
            if (resp && (resp.changed || avancou)) {
              await carregarLista(cur);
              await sleep(20);
            } else {
              // sem mudanças → pequena folga antes do próximo long-poll
              await sleep(AWAIT_GAP_MS);
            }

            continue;
          }

          // --------- Fallback por HEAD/ETag ---------
          const vNow = await headVersao();
          const cur2 = versaoRef.current;

          if (vNow !== null && cur2 !== null && vNow === cur2) {
            await sleep(POLL_FALLBACK_MS);
            continue;
          }
          if (vNow === null && cur2 !== null) {
            await sleep(POLL_FALLBACK_MS);
          }

          await carregarLista(cur2);
        }
      } catch (e) {
        if (!stop.current)
          console.error("useProdutosDisponiveis loop error:", e);
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };
  }, []);

  const refresh = async () => {
    while (fetchingRef.current) await sleep(25);
    await carregarLista(versaoRef.current);
  };

  return { produtos, loading, refresh, fonte, versao };
}
